import { Group, SimpleGrid, Stack } from '@mantine/core';
import type { CeleryQueue, CeleryTask, CeleryWorker } from '@repo/api-codegen';
import {
	useApiGetCeleryQueues,
	useApiGetCeleryTasks,
	useApiGetCeleryWorkers,
} from '@repo/api-codegen';
import { Button, Title } from '@repo/foundations';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FullWidthLoadingSpinner } from '../../../components/LoadingSpinner';
import { QueueCard } from './components/QueueCard';
import { QueueDetailDrawer } from './components/QueueDetailDrawer';
import { TaskDetailDrawer } from './components/TaskDetailDrawer';
import { WorkerCard } from './components/WorkerCard';
import { WorkerDetailDrawer } from './components/WorkerDetailDrawer';
import { useStyles } from './InspectCeleryPage.styles';

function InspectCeleryPage() {
	const { theme } = useStyles();

	const [selectedWorker, setSelectedWorker] = useState<CeleryWorker | null>(
		null
	);
	const [selectedQueue, setSelectedQueue] = useState<CeleryQueue | null>(null);
	const [selectedTask, setSelectedTask] = useState<CeleryTask | null>(null);

	const {
		data: workersData,
		isLoading: workersLoading,
		isFetching: workersFetching,
		queryKey: workersQueryKey,
	} = useApiGetCeleryWorkers({});
	const {
		data: queuesData,
		isLoading: queuesLoading,
		isFetching: queuesFetching,
		queryKey: queuesQueryKey,
	} = useApiGetCeleryQueues({});
	const {
		data: tasksData,
		isLoading: tasksLoading,
		isFetching: tasksFetching,
		queryKey: tasksQueryKey,
	} = useApiGetCeleryTasks({});

	const queryClient = useQueryClient();
	const isLoading = workersLoading || queuesLoading || tasksLoading;
	const isRefreshing = workersFetching || queuesFetching || tasksFetching;

	const handleRefresh = () => {
		queryClient.invalidateQueries({ queryKey: workersQueryKey });
		queryClient.invalidateQueries({ queryKey: queuesQueryKey });
		queryClient.invalidateQueries({ queryKey: tasksQueryKey });
	};

	// Map of queueName -> tasks
	const tasksByQueue = useMemo(() => {
		if (!tasksData) return {};

		const result: { [queueName: string]: CeleryTask[] } = {};

		tasksData.forEach((task) => {
			const queueName = task.delivery_info.routing_key;
			if (!queueName) {
				return;
			}

			if (!result[queueName]) {
				result[queueName] = [];
			}
			result[queueName].push(task);
		});

		// Sort by time_start (newest first)
		Object.values(result).forEach((tasks) =>
			tasks.sort((a, b) => (b.time_start || 0) - (a.time_start || 0))
		);

		return result;
	}, [tasksData]);

	if (isLoading) {
		return <FullWidthLoadingSpinner />;
	}

	return (
		<Stack spacing={theme.spacing['3xl']}>
			<Stack>
				<Group position="left">
					<Button
						variant="default"
						size="md"
						onClick={handleRefresh}
						loading={isRefreshing}
					>
						Refresh
					</Button>
				</Group>

				{/* Workers */}
				<Stack spacing="lg">
					<Title size="lg" weight="bold">
						Workers
					</Title>
					<SimpleGrid cols={3} spacing="lg">
						{(workersData || []).map((worker) => (
							<WorkerCard
								key={worker.name}
								worker={worker}
								onClick={() => setSelectedWorker(worker)}
							/>
						))}
					</SimpleGrid>
				</Stack>

				{/* Queues & Tasks */}
				<Stack spacing="lg">
					<Title size="lg" weight="bold" color="text/primary/default">
						Queues
					</Title>
					{(queuesData || []).map((queue) => (
						<QueueCard
							key={queue.name}
							queue={queue}
							tasks={tasksByQueue[queue.name] || []}
							onQueueClick={() => setSelectedQueue(queue)}
							onTaskClick={setSelectedTask}
						/>
					))}
				</Stack>
			</Stack>

			{/* Detail Drawers */}
			<WorkerDetailDrawer
				worker={selectedWorker}
				onClose={() => setSelectedWorker(null)}
			/>
			<QueueDetailDrawer
				queue={selectedQueue}
				onClose={() => setSelectedQueue(null)}
			/>
			<TaskDetailDrawer
				task={selectedTask}
				onClose={() => setSelectedTask(null)}
			/>
		</Stack>
	);
}

export default InspectCeleryPage;
